import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SettingsPageHeader, FloatingSaveButton } from '../components';
import { createAppResourceService } from '@/global';
import JsonEditor from '@/components/JsonEditor';
import AddIcon from '@mui/icons-material/Add';

// Create language service - language translations are just objects, not Resources
const langService = createAppResourceService<any>("meta/lang");

const I18nSettings = () => {
  const { t, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [translationData, setTranslationData] = useState<string>('{}');
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  // Use the language service to get and update translations
  const { data: langData, isLoading, refetch } = langService.useGetOne(selectedLanguage, {
    enabled: !!selectedLanguage,
  });
  const { mutateAsync: updateLang, isLoading: isSaving } = langService.useUpdate();

  // Available languages - English is default and always exists
  const availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'nl', name: 'Dutch' },
    { code: 'sv', name: 'Swedish' },
    { code: 'no', name: 'Norwegian' },
    { code: 'da', name: 'Danish' },
  ];

  // Load translation data when language data changes
  useEffect(() => {
    if (langData) {
      // Use data from backend
      const jsonString = JSON.stringify(langData, null, 2);
      setTranslationData(jsonString);
      setIsDirty(false);
    } else if (!isLoading) {
      // Fallback to i18n resources if not in backend
      const resources = i18n.getResourceBundle(selectedLanguage, 'translation') || {};
      const jsonString = JSON.stringify(resources, null, 2);
      setTranslationData(jsonString);
      setIsDirty(false);
    }
  }, [langData, selectedLanguage, isLoading, i18n]);

  // Handle language selection change
  const handleLanguageChange = (event: any) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
  };

  // Handle JSON editor changes
  const handleJsonChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTranslationData(event.target.value);
    setIsDirty(true);
  };

  // Handle save
  const handleSave = async () => {
    setError(null);
    
    try {
      // Validate JSON
      const parsedData = JSON.parse(translationData);
      
      // Save to backend using lang service
      await updateLang({ id: selectedLanguage, payload: parsedData });
      
      setIsDirty(false);
      // Reload the data to ensure consistency
      await refetch();
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setError(`Invalid JSON: ${err.message}`);
      } else {
        setError(`Failed to save translations: ${err?.message || err}`);
      }
    }
  };

  // Handle add new language
  const handleAddLanguage = () => {
    // TODO: Implement add new language functionality
    console.log('Add new language functionality to be implemented');
  };

  const selectedLanguageName = availableLanguages.find(lang => lang.code === selectedLanguage)?.name || selectedLanguage;

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3, pb: 10 }}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          {/* Header */}
          <SettingsPageHeader 
            title="Translation Management" 
            isDirty={isDirty} 
          />

          {/* Language Selection */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={3}>
                <Typography variant="h6">Language Selection</Typography>
                
                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Select Language</InputLabel>
                    <Select
                      value={selectedLanguage}
                      onChange={handleLanguageChange}
                      label="Select Language"
                    >
                      {availableLanguages.map((language) => (
                        <MenuItem key={language.code} value={language.code}>
                          {language.name} ({language.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddLanguage}
                  >
                    Add New Language
                  </Button>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Currently editing: <strong>{selectedLanguageName}</strong> ({selectedLanguage})
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Error Display */}
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {/* Translation Editor */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">
                  Translation Data - {selectedLanguageName}
                </Typography>
                
                {isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading translations...</Typography>
                  </Box>
                ) : (
                  <JsonEditor
                    label="Translation JSON"
                    value={translationData}
                    onChange={handleJsonChange}
                    height="500px"
                    helperText="Edit the translation data in JSON format. Changes will be saved when you click Save."
                  />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Floating Save Button */}
        <FloatingSaveButton 
          show={isDirty && !isLoading} 
          onSave={handleSave} 
          isSaving={isSaving} 
        />
      </Box>
    </Box>
  );
};

export { I18nSettings };
